import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { lighten, useTheme } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import LoginForm from './LogInForm';
import studentImg from '../../../shared/img/Student.png';
import learnerImg from '../../../shared/img/teacher.png';
import EWClientImg from '../../../shared/img/company.png';
import config from '../../../config';

const AccessSelector = ({
  handleSubmit,
  loading,
  userEmail,
  adminEmail,
  impersonateType,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const options = [
    {
      type: 'student',
      label: t('login.access.student', 'Apprenants'),
      image: studentImg,
    },
    {
      type: 'learner',
      label: t('login.access.learner', 'Formateur English World'),
      image: learnerImg,
    },
    {
      type: 'ew_client',
      label: t('login.access.client', 'Client English World / Medical World'),
      image: EWClientImg,
    },
  ];

  const [selectedType, setSelectedType] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (
      impersonateType
      && userEmail
      && adminEmail
      && options.find((opt) => opt.type === impersonateType)
    ) {
      setSelectedType(impersonateType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateType, userEmail, adminEmail]);

  const isImpersonate = !!(userEmail && adminEmail && impersonateType);

  return (
    <div className="access-container">
      {!selectedType && (
        <div className="page-header">
          <div className="account__head">
            <h3 className="account__title">
              {t('login.welcomeMessage', 'Welcome to ')}
              <span className="account__logo">
                {t('login.english', 'English')}
                <span className="account__logo-accent">
                  {t('login.world', 'World')}
                </span>
              </span>
            </h3>
          </div>
        </div>
      )}

      {!selectedType && (
        <div className="image-section full-width" style={{ marginTop: '5%' }}>
          <div className="image-grid">
            {options.map((option) => (
              <div
                key={option.type}
                role="button"
                tabIndex={0}
                aria-pressed={false}
                onClick={() => setSelectedType(option.type)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedType(option.type);
                  }
                }}
                onMouseEnter={() => setHovered(option.type)}
                onMouseLeave={() => setHovered(null)}
                className="image-option"
              >
                <div
                  className="image-wrapper"
                  style={{
                    border:
                      hovered === option.type
                        ? `2px solid ${lighten(primaryColor, 0.2)}`
                        : '2px solid #D7D4D8',
                    transform: hovered === option.type ? 'scale(1.03)' : 'scale(1)',
                    boxShadow:
                      hovered === option.type
                        ? '0 4px 12px #D7D4D8'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <img src={option.image} alt={option.label} className="image" />
                </div>
                <div className="image-label">{option.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedType && (
        <div className="login-section">
          <div
            className="login-image-side"
            style={{
              width: '55%',
              height: '100%',
              borderTopLeftRadius: 40,
              borderBottomLeftRadius: 40,
              backgroundImage: `url(${config.OBJECT_URL}/thumbnail/client_access_landing.png)`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="form-section" style={{ width: '45%' }}>
            <div
              className="account__head form-header"
              style={{
                marginTop: '30px',
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h3 className="account__title">
                {t('login.welcomeMessage', 'Welcome to ')}
                <span className="account__logo">
                  {t('login.english', 'English')}
                  <span className="account__logo-accent">
                    {t('login.world', 'World')}
                  </span>
                </span>
              </h3>

              <Button
                variant="contained"
                style={{
                  backgroundColor: '#003366',
                  color: '#fff',
                  textTransform: 'none',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#336699';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#003366';
                }}
                onClick={() => setSelectedType(null)}
              >
                {t('common.back', 'Retour')}
              </Button>
            </div>

            <div className="login-form-container">
              <LoginForm
                onSubmit={handleSubmit}
                loading={loading}
                accountType={selectedType}
                impersonate={isImpersonate}
                userEmail={userEmail}
                adminEmail={adminEmail}
              />
            </div>
          </div>
        </div>
      )}

      <style jsx>
        {`
          .access-container {
            display: flex;
            flex-direction: column;
            width: 75%;
            margin: 10% auto;
            min-height: 600px;
            max-height: 100vh;
            box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.3);
            border-radius: 40px;
            overflow-y: auto;
            background-color: white;
          }
          .login-section {
            display: flex;
            flex-direction: row;
            width: 100%;
            height: 100%;
          }
          .page-header {
            display: block;
            padding-top: 30px;
            display: flex;
            justify-content: center;
          }
          .image-section.full-width {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
          }
          .image-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 60px;
            width: 100%;
            max-width: 800px;
          }
          .image-option {
            display: flex;
            flex-direction: column;
            align-items: center;
            cursor: pointer;
            outline: none;
          }
          .image-wrapper {
            padding: 20px;
            border-radius: 12px;
            transition: all 0.3s ease;
            width: 180px;
            height: 180px;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .image {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
          .image-label {
            margin-top: 15px;
            font-size: 16px;
            text-align: center;
            color: #333;
            font-weight: 500;
          }
          .form-section {
            padding: 40px 30px;
            display: flex;
            flex-direction: column;
            transition: all 0.3s ease;
            background-color: white;
            border-top-right-radius: 40px;
            border-bottom-right-radius: 40px;
          }
          @media (max-width: 768px) {
            .page-header {
              padding: 10px;
              justify-content: flex-start;
            }
            .form-header {
              display: none;
            }
            .image-grid {
              grid-template-columns: 1fr;
              gap: 30px;
              width: 100%;
            }
            .access-container {
              flex-direction: column;
              width: 95%;
              border-radius: 20px;
              margin-top: 5%;
              box-shadow: none;
              border: none;
              overflow-y: auto;
            }
            .form-section {
              width: 100% !important;
              padding: 20px 15px;
              border-radius: 20px;
            }
            .login-section {
              flex-direction: column;
              margin-top: 35%;
            }
            .login-image-side {
              display: none;
            }
            .image-wrapper {
              width: 150px;
              height: 150px;
            }
            .image-label {
              font-size: 18px;
            }
          }
        `}
      </style>
    </div>
  );
};

AccessSelector.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  userEmail: PropTypes.string,
  adminEmail: PropTypes.string,
  impersonateType: PropTypes.string,
};

AccessSelector.defaultProps = {
  userEmail: '',
  adminEmail: '',
  impersonateType: '',
};

export default AccessSelector;
